import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import TitreSection from './TitreSection';
import Reveal from './Reveal';
import NoteEtoiles from './NoteEtoiles';
import { chargerAvis, sAbonner, instantane, encoder } from './avisService';
import { MACHINES } from '../data/site';

const VIDE = {
  note: 0,
  nom: '',
  societe: '',
  commune: '',
  machine: '',
  avis: '',
  chantier_mois: '',
  chantier_annee: '',
  affichage: 'nom-entreprise',
  email: '',
  consentement: false,
  'site-web': '',
};

function formaterDate(iso) {
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch (e) {
    return '';
  }
}

// La date du chantier est saisie au mois : personne ne se souvient du jour
// exact, et le mois suffit largement a situer l'experience.
function formaterMois(aaaaMm) {
  if (!aaaaMm) return '';
  const [a, m] = String(aaaaMm).split('-');
  const d = new Date(Number(a), Number(m) - 1, 1);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
}

function moisCourant() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const MOIS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

// Sept annees en arriere : cela couvre largement l'anciennete de l'entreprise.
function anneesPossibles() {
  const a = new Date().getFullYear();
  return Array.from({ length: 7 }, (_, i) => a - i);
}

function CarteAvis({ a }) {
  return (
    <article className="carte p-6 h-full flex flex-col">
      <NoteEtoiles note={a.note} taille={17} classe="text-jaune" />
      <p className="mt-4 text-nuit/85 text-[16px] flex-1 whitespace-pre-line">{a.texte}</p>
      <footer className="mt-5 pt-4 border-t border-nuit/12">
        <p className="font-cond uppercase font-bold tracking-[0.03em] text-[15.5px] text-nuit">
          {a.auteur}
          {a.societe ? <span className="text-bleu">, {a.societe}</span> : null}
        </p>
        <p className="mt-1 text-[14px] text-beton">
          {[a.commune, a.machine].filter(Boolean).join(' · ')}
        </p>
        {a.chantier && (
          <p className="mt-1 font-cond uppercase tracking-[0.06em] text-[13px] text-bleu">
            Chantier de {formaterMois(a.chantier)}
          </p>
        )}
        <p className="mt-1.5 text-[12.5px] text-beton/70">
          Avis publié le {formaterDate(a.date)}
        </p>
      </footer>
    </article>
  );
}

function Etoiles({ valeur, onChange }) {
  const [survol, setSurvol] = useState(0);
  const affiche = survol || valeur;
  return (
    <div className="flex items-center gap-1" onMouseLeave={() => setSurvol(0)}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          onMouseEnter={() => setSurvol(n)}
          aria-label={`${n} étoile${n > 1 ? 's' : ''} sur 5`}
          aria-pressed={valeur === n}
          className={`p-1 rounded-[3px] transition-colors ${
            n <= affiche ? 'text-jaune' : 'text-nuit/25'
          } hover:text-orange focus-visible:outline focus-visible:outline-2 focus-visible:outline-bleu`}
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 2.4l2.9 5.9 6.5.95-4.7 4.58 1.11 6.47L12 17.25 6.19 20.3l1.11-6.47L2.6 9.25l6.5-.95L12 2.4z" />
          </svg>
        </button>
      ))}
      <span className="ml-2 font-cond uppercase tracking-[0.06em] text-[13px] text-beton">
        {valeur ? `${valeur} sur 5` : 'Votre note'}
      </span>
    </div>
  );
}

export default function Avis({ compact = false }) {
  const [etat, setEtat] = useState(instantane());
  const [ouvert, setOuvert] = useState(false);
  const [valeurs, setValeurs] = useState(VIDE);
  const [envoi, setEnvoi] = useState('repos');
  const [erreur, setErreur] = useState('');
  const [affiches, setAffiches] = useState(compact ? 3 : 8);

  useEffect(() => {
    const desabonner = sAbonner(setEtat);
    chargerAvis();
    return desabonner;
  }, []);

  const maj = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setValeurs((v) => ({ ...v, [name]: type === 'checkbox' ? checked : value }));
  }, []);

  async function envoyer(e) {
    e.preventDefault();
    setErreur('');
    if (!valeurs.note) return setErreur('Merci de choisir une note de 1 à 5 étoiles.');
    if (valeurs.avis.trim().length < 20) return setErreur('Votre avis doit faire au moins vingt caractères.');
    if (!valeurs.chantier_mois || !valeurs.chantier_annee) {
      return setErreur('Merci d\'indiquer le mois et l\'année de votre chantier.');
    }
    const chantier = `${valeurs.chantier_annee}-${valeurs.chantier_mois}`;
    if (chantier > moisCourant()) return setErreur('La date du chantier ne peut pas être dans le futur.');
    if (!/.+@.+\..+/.test(valeurs.email)) return setErreur('Merci d\'indiquer une adresse mail valide, elle ne sera pas publiée.');
    if (!valeurs.consentement) return setErreur('Merci de cocher la case de consentement pour publier votre avis.');

    setEnvoi('envoi');
    try {
      const r = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encoder({
          'form-name': 'avis',
          ...valeurs,
          chantier_date: chantier,
          consentement: valeurs.consentement ? 'oui' : 'non',
        }),
      });
      if (!r.ok) throw new Error('envoi refusé');
      setEnvoi('ok');
      setValeurs(VIDE);
      setOuvert(false);
    } catch (err) {
      setEnvoi('erreur');
      setErreur("L'envoi n'a pas abouti. Réessayez dans un instant, ou appelez-nous.");
    }
  }

  const champ =
    'w-full border border-nuit/20 rounded-[3px] px-3.5 py-2.5 text-[16px] text-nuit bg-white ' +
    'focus:border-bleu focus:outline-none focus:ring-2 focus:ring-bleu/25';
  const etiquette = 'block font-cond uppercase tracking-[0.07em] text-[13px] text-beton mb-1.5';

  const aDesAvis = etat.total > 0;
  const listeAffichee = etat.avis.slice(0, affiches);
  const reste = etat.avis.length - listeAffichee.length;

  return (
    <section className="bg-trame py-16 sm:py-20" id="avis">
      <div className="conteneur">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <TitreSection avant="La parole aux " mot="chantiers" />
            {aDesAvis ? (
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <NoteEtoiles note={etat.moyenne} taille={22} classe="text-jaune" />
                <span className="font-display text-[26px] text-nuit leading-none">
                  {String(etat.moyenne).replace('.', ',')}
                  <span className="text-beton text-[18px]"> sur 5</span>
                </span>
                <span className="font-cond uppercase tracking-[0.07em] text-[14px] text-beton">
                  {etat.total} avis {etat.total > 1 ? 'publiés' : 'publié'}
                </span>
              </div>
            ) : (
              <p className="mt-4 max-w-2xl text-nuit/75">
                Aucun avis publié pour le moment. Si vous avez loué chez nous, votre retour aidera
                les prochains à choisir leur machine en confiance.
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={() => setOuvert((o) => !o)}
            className="btn-jaune self-start lg:self-auto whitespace-nowrap"
            aria-expanded={ouvert}
            aria-controls="formulaire-avis"
          >
            {ouvert ? 'Fermer' : 'Laisser un avis'}
          </button>
        </div>

        {envoi === 'ok' && (
          <p className="mt-6 carte p-5 border-l-4 border-l-jaune text-nuit/85">
            <strong className="text-nuit">Merci, votre avis nous est bien parvenu.</strong> Il est
            en cours de vérification : nous contrôlons qu'il correspond bien à une location réelle
            avant de le publier, généralement sous deux jours ouvrés. Vous n'avez rien d'autre à faire.
          </p>
        )}

        {ouvert && (
          <div id="formulaire-avis" className="mt-8 carte p-6 sm:p-8">
            <h3 className="font-display uppercase text-[24px] text-nuit">Votre avis</h3>
            <p className="mt-2 text-nuit/75 text-[16px] max-w-2xl">
              Votre avis est vérifié avant publication : nous contrôlons qu'il correspond à une
              location réelle. Comptez deux jours ouvrés. Un avis négatif portant sur une prestation
              que nous avons effectuée est publié comme les autres.
            </p>

            <form onSubmit={envoyer} className="mt-7" noValidate>
              <p className="hidden" aria-hidden="true">
                <label>
                  Ne remplissez pas ce champ
                  <input name="site-web" value={valeurs['site-web']} onChange={maj} tabIndex="-1" autoComplete="off" />
                </label>
              </p>

              <div className="mb-6">
                <span className={etiquette}>Votre note</span>
                <Etoiles valeur={valeurs.note} onChange={(n) => setValeurs((v) => ({ ...v, note: n }))} />
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className={etiquette} htmlFor="avis-nom">Votre nom</label>
                  <input id="avis-nom" name="nom" required value={valeurs.nom} onChange={maj} className={champ} autoComplete="name" />
                </div>
                <div>
                  <label className={etiquette} htmlFor="avis-societe">Votre entreprise</label>
                  <input id="avis-societe" name="societe" value={valeurs.societe} onChange={maj} className={champ} autoComplete="organization" />
                </div>
                <div>
                  <label className={etiquette} htmlFor="avis-commune">Commune du chantier</label>
                  <input id="avis-commune" name="commune" value={valeurs.commune} onChange={maj} className={champ} placeholder="Ex. Créteil" />
                </div>
                <div>
                  <span className={etiquette}>Date du chantier</span>
                  <div className="flex gap-3">
                    <select
                      aria-label="Mois du chantier" value={valeurs.chantier_mois}
                      onChange={(e) => setValeurs((v) => ({ ...v, chantier_mois: e.target.value }))}
                      className={champ}
                    >
                      <option value="">Mois</option>
                      {MOIS.map((m, i) => (
                        <option key={m} value={String(i + 1).padStart(2, '0')}>{m}</option>
                      ))}
                    </select>
                    <select
                      aria-label="Année du chantier" value={valeurs.chantier_annee}
                      onChange={(e) => setValeurs((v) => ({ ...v, chantier_annee: e.target.value }))}
                      className={champ}
                    >
                      <option value="">Année</option>
                      {anneesPossibles().map((a) => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className={etiquette} htmlFor="avis-machine">Machine louée</label>
                  <select id="avis-machine" name="machine" value={valeurs.machine} onChange={maj} className={champ}>
                    <option value="">Non précisé</option>
                    {MACHINES.map((m) => (
                      <option key={m.slug} value={m.nom}>{m.nom}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-5">
                <label className={etiquette} htmlFor="avis-email">
                  Votre adresse mail <span className="normal-case tracking-normal">(jamais publiée)</span>
                </label>
                <input
                  id="avis-email" name="email" type="email" required
                  value={valeurs.email} onChange={maj} className={champ} autoComplete="email"
                />
                <p className="mt-1.5 text-[13.5px] text-beton">
                  Elle nous sert uniquement à vérifier votre location et à vous répondre si votre
                  avis ne pouvait pas être publié.
                </p>
              </div>

              <div className="mt-5">
                <label className={etiquette} htmlFor="avis-texte">Votre avis</label>
                <textarea
                  id="avis-texte" name="avis" rows="5" required
                  value={valeurs.avis} onChange={maj} className={champ}
                  placeholder="Le chantier, la machine, la livraison, ce qui s'est bien passé ou moins bien."
                />
              </div>

              <div className="mt-5">
                <label className={etiquette} htmlFor="avis-affichage">Comment souhaitez-vous apparaître</label>
                <select id="avis-affichage" name="affichage" value={valeurs.affichage} onChange={maj} className={champ}>
                  <option value="nom-entreprise">Mon nom et mon entreprise</option>
                  <option value="entreprise">Mon entreprise seule</option>
                  <option value="prenom">Mon prénom et l'initiale de mon nom</option>
                  <option value="anonyme">Anonyme, sans nom ni entreprise</option>
                </select>
              </div>

              <label className="mt-5 flex gap-3 items-start text-[15px] text-nuit/85">
                <input
                  type="checkbox" name="consentement" required
                  checked={valeurs.consentement} onChange={maj}
                  className="mt-1 w-5 h-5 shrink-0 accent-jaune"
                />
                <span>
                  J'accepte que mon avis soit publié sur ce site, avec l'identité que j'ai choisie ci-dessus.
                  Je peux demander leur retrait à tout moment en écrivant à l'adresse indiquée dans la{' '}
                  <Link to="/confidentialite" className="text-bleu underline">politique de confidentialité</Link>.
                </span>
              </label>

              {erreur && <p className="mt-4 text-[15px] text-orange font-semibold">{erreur}</p>}

              <button type="submit" className="btn-jaune mt-7" disabled={envoi === 'envoi'}>
                {envoi === 'envoi' ? 'Publication…' : 'Publier mon avis'}
              </button>
            </form>
          </div>
        )}

        {aDesAvis && (
          <>
            <div className="mt-9 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {listeAffichee.map((a, i) => (
                <Reveal key={a.id} delai={Math.min(i * 0.05, 0.25)}>
                  <CarteAvis a={a} />
                </Reveal>
              ))}
            </div>

            {reste > 0 && (
              <div className="mt-8 flex flex-col items-center gap-3">
                <button type="button" onClick={() => setAffiches((n) => n + 8)} className="btn-ligne">
                  Voir plus d'avis
                </button>
                <p className="font-cond uppercase tracking-[0.08em] text-[12.5px] text-beton">
                  {listeAffichee.length} avis sur {etat.avis.length}
                </p>
              </div>
            )}
          </>
        )}

        <p className="mt-8 text-[14px] text-beton max-w-3xl">
          Avis vérifiés : chaque avis est rapproché d'une location réelle avant sa publication,
          et publié sous deux jours ouvrés. Classement par date de chantier, de la plus récente à
          la plus ancienne. La date de publication figure sous chaque avis. Aucune
          contrepartie n'est offerte en échange d'un avis. Les conditions de publication, de refus
          et de retrait sont détaillées dans notre{' '}
          <Link to="/charte-avis" className="text-bleu underline">charte des avis</Link>.
        </p>
      </div>
    </section>
  );
}
